import type { NavigateFunction } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import api from '../../services/api';
import { APP_CONFIG } from '../../config/appConfig';
import { createAppointment, createTodo } from '../../services/mutations';
import { emailPartitionAtSearch } from '../emails/utils/emailPartitionAtQuery';
import type {
  AiDraftFromEmailResult,
  AiModelOption,
  AiNextAction,
  AiSmartReplyTemplate,
} from './types';
import {
  AI_FORM_PREFILL_STATE_KEY,
  saveAiFormPrefill,
  type AiFormPrefillTarget,
} from './aiFormPrefill';

export function useAiAssistantEnabled(): boolean {
  const { applicationSettings } = useSettings();
  return applicationSettings?.enableAiAssistant === true;
}

export function useAiEmailsEnabled(): boolean {
  const { applicationSettings } = useSettings();
  return applicationSettings?.enableAiEmails === true;
}

export async function fetchAssistantModels(): Promise<AiModelOption[]> {
  const res = await api.get<{ models: AiModelOption[] }>('/ai/assistant/models');
  return res.data.models ?? [];
}

export async function summarizeClientHistory(
  clientId: number,
  model?: string,
): Promise<string> {
  const res = await api.post<{ summary: string }>(
    `/ai/assistant/client-history/${clientId}`,
    { model },
    { timeout: APP_CONFIG.api.aiAssistantTimeout },
  );
  return res.data.summary;
}

export async function draftFromEmail(params: {
  emailId: number;
  partitionAt?: string | null;
  draftType: 'todo' | 'appointment';
  model?: string;
}): Promise<AiDraftFromEmailResult> {
  const res = await api.post<AiDraftFromEmailResult>(
    '/ai/assistant/draft-from-email',
    params,
    { timeout: APP_CONFIG.api.aiAssistantTimeout },
  );
  return res.data;
}

export async function fetchNextActions(model?: string): Promise<AiNextAction[]> {
  const res = await api.post<{ actions: AiNextAction[] }>(
    '/ai/assistant/next-actions',
    { model },
    { timeout: APP_CONFIG.api.aiAssistantTimeout },
  );
  return res.data.actions ?? [];
}

export async function fetchSmartReplyTemplates(params: {
  emailId?: number;
  partitionAt?: string | null;
  clientId?: number;
  model?: string;
}): Promise<AiSmartReplyTemplate[]> {
  const res = await api.post<{ templates: AiSmartReplyTemplate[] }>(
    '/ai/assistant/smart-reply-templates',
    params,
    { timeout: APP_CONFIG.api.aiAssistantTimeout },
  );
  return res.data.templates ?? [];
}

/** Build a new-record URL with query params for GenericFormPage prefill. */
export function buildPrefillRoute(
  basePath: string,
  fields: Record<string, string | number | undefined | null>,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}

/** Resolve prefill URL from an AI draft (todo or appointment). */
export function buildAiDraftFormRoute(result: AiDraftFromEmailResult): string | null {
  if (result.draftType === 'appointment' && result.appointment?.clientAppName) {
    return buildPrefillRoute('/appointments/new', {
      clientId: result.appointment.clientId ?? undefined,
      clientAppName: result.appointment.clientAppName,
      clientAppScheduleDateTime: result.appointment.clientAppScheduleDateTime ?? undefined,
    });
  }
  if (result.draftType === 'todo' && result.todo?.todoDescr) {
    return buildPrefillRoute('/todos/new', {
      clientId: result.todo.clientId ?? undefined,
      todoDescr: result.todo.todoDescr,
      todoDueDate: result.todo.todoDueDate ?? undefined,
    });
  }
  if (result.appointment?.clientAppName) {
    return buildPrefillRoute('/appointments/new', {
      clientId: result.appointment.clientId ?? undefined,
      clientAppName: result.appointment.clientAppName,
      clientAppScheduleDateTime: result.appointment.clientAppScheduleDateTime ?? undefined,
    });
  }
  if (result.todo?.todoDescr) {
    return buildPrefillRoute('/todos/new', {
      clientId: result.todo.clientId ?? undefined,
      todoDescr: result.todo.todoDescr,
      todoDueDate: result.todo.todoDueDate ?? undefined,
    });
  }
  return null;
}

/** Field map for sessionStorage prefill (full text, no URL length limits). */
export function getAiDraftPrefillFields(result: AiDraftFromEmailResult): {
  target: AiFormPrefillTarget;
  fields: Record<string, string | number | null | undefined>;
} | null {
  if (result.draftType === 'appointment' && result.appointment?.clientAppName) {
    return {
      target: '/appointments/new',
      fields: {
        clientId: result.appointment.clientId ?? undefined,
        clientAppName: result.appointment.clientAppName,
        clientAppScheduleDateTime: result.appointment.clientAppScheduleDateTime ?? undefined,
      },
    };
  }
  if (result.draftType === 'todo' && result.todo?.todoDescr) {
    return {
      target: '/todos/new',
      fields: {
        clientId: result.todo.clientId ?? undefined,
        todoDescr: result.todo.todoDescr,
        todoDueDate: result.todo.todoDueDate ?? undefined,
      },
    };
  }
  if (result.appointment?.clientAppName) {
    return {
      target: '/appointments/new',
      fields: {
        clientId: result.appointment.clientId ?? undefined,
        clientAppName: result.appointment.clientAppName,
        clientAppScheduleDateTime: result.appointment.clientAppScheduleDateTime ?? undefined,
      },
    };
  }
  if (result.todo?.todoDescr) {
    return {
      target: '/todos/new',
      fields: {
        clientId: result.todo.clientId ?? undefined,
        todoDescr: result.todo.todoDescr,
        todoDueDate: result.todo.todoDueDate ?? undefined,
      },
    };
  }
  return null;
}

export type AiDraftEntityType = 'todo' | 'appointment';

export function resolveAiDraftEntityType(result: AiDraftFromEmailResult): AiDraftEntityType | null {
  if (result.draftType === 'appointment' && result.appointment?.clientAppName) return 'appointment';
  if (result.draftType === 'todo' && result.todo?.todoDescr) return 'todo';
  if (result.appointment?.clientAppName) return 'appointment';
  if (result.todo?.todoDescr) return 'todo';
  return null;
}

function extractCreatedEntityId(result: unknown, entityType: AiDraftEntityType): number | null {
  const root = result && typeof result === 'object' ? (result as Record<string, unknown>) : null;
  const payload =
    root?.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root;
  if (!payload) return null;
  const key = entityType === 'todo' ? 'todoId' : 'clientAppId';
  const candidate = payload[key] ?? payload.id;
  return typeof candidate === 'number' && Number.isFinite(candidate) ? candidate : null;
}

/** Build API write payload from an AI draft result. */
export function buildAiDraftSavePayload(
  result: AiDraftFromEmailResult,
): Record<string, string | number | boolean | null | undefined> | null {
  const entityType = resolveAiDraftEntityType(result);
  if (entityType === 'todo' && result.todo?.todoDescr) {
    return {
      todoDescr: result.todo.todoDescr,
      todoCompleted: false,
      clientId: result.todo.clientId ?? undefined,
      todoDueDate: result.todo.todoDueDate ?? undefined,
    };
  }
  if (entityType === 'appointment' && result.appointment?.clientAppName) {
    return {
      clientAppName: result.appointment.clientAppName,
      clientId: result.appointment.clientId ?? undefined,
      clientAppScheduleDateTime: result.appointment.clientAppScheduleDateTime ?? undefined,
    };
  }
  return null;
}

/** Create todo/appointment from AI draft and optionally link to the source email. */
export async function saveAiDraftDirectly(params: {
  draft: AiDraftFromEmailResult;
  emailId?: number;
  partitionAt?: string | null;
}): Promise<{ entityType: AiDraftEntityType; entityId: number }> {
  const entityType = resolveAiDraftEntityType(params.draft);
  const payload = buildAiDraftSavePayload(params.draft);
  if (!entityType || !payload) {
    throw new Error('Draft data is incomplete');
  }

  const created =
    entityType === 'todo' ? await createTodo(payload) : await createAppointment(payload);
  const entityId = extractCreatedEntityId(created, entityType);
  if (!entityId) {
    throw new Error('Saved but could not read the new record id');
  }

  if (params.emailId) {
    const partitionQuery = emailPartitionAtSearch(params.partitionAt);
    if (entityType === 'todo') {
      await api.post(`/emails/${params.emailId}/link-todo/${entityId}${partitionQuery}`);
    } else {
      await api.post(`/emails/${params.emailId}/link-appointment/${entityId}${partitionQuery}`);
    }
    void api.get(`/emails/${params.emailId}${partitionQuery}`).catch(() => undefined);
  }

  return { entityType, entityId };
}

/** Save AI draft and navigate to the new todo/appointment form with prefill state. */
export function openAiDraftForm(
  navigate: NavigateFunction,
  result: AiDraftFromEmailResult,
): boolean {
  const prefill = getAiDraftPrefillFields(result);
  if (!prefill) return false;
  saveAiFormPrefill(prefill.target, prefill.fields);
  navigate(`${prefill.target}?fromAi=1`, {
    state: { [AI_FORM_PREFILL_STATE_KEY]: prefill.fields },
  });
  return true;
}

/** @deprecated Use openAiDraftForm — returns path only, no router state. */
export function prepareAiDraftFormOpen(result: AiDraftFromEmailResult): AiFormPrefillTarget | null {
  const prefill = getAiDraftPrefillFields(result);
  if (!prefill) return null;
  saveAiFormPrefill(prefill.target, prefill.fields);
  return prefill.target;
}
